// ─── Wiki SOEM — contenido por módulos ────────────────────────────────────────
// Contenido estático versionado con el código. Cada módulo declara los roles
// a los que aplica; la página Wiki filtra por rol activo (con opción de ver todo).
//
// Tipos de bloque soportados por el renderer (pages/Wiki.jsx):
//   { t: 'p',     text }                      → párrafo (admite **negritas**)
//   { t: 'h',     text }                      → subtítulo de sección
//   { t: 'steps', items: [] }                 → lista numerada de pasos
//   { t: 'list',  items: [] }                 → lista con viñetas
//   { t: 'tip',   text }                      → nota informativa (azul)
//   { t: 'warn',  text }                      → advertencia (ámbar)
//   { t: 'flow',  text }                      → diagrama de cadena en monospace
//   { t: 'table', headers: [], rows: [[]] }   → tabla simple

import {
  BookOpen, ShieldCheck, LayoutDashboard, Building2, FolderKanban,
  CalendarDays, ClipboardList, Layers, Map, Users, QrCode, Smartphone,
  Monitor, Settings, Lightbulb, LifeBuoy, ShieldAlert, Bell, FileText,
} from 'lucide-react';

export const ALL_ROLES = ['super_admin', 'lider_proyecto', 'levantador', 'cliente'];
const MGMT = ['super_admin', 'lider_proyecto', 'levantador'];

export const WIKI_MODULES = [
  {
    id: 'introduccion',
    title: '¿Qué es SOEM?',
    icon: BookOpen,
    roles: ALL_ROLES,
    summary: 'Qué es la plataforma, para qué sirve y cómo se organiza todo.',
    blocks: [
      { t: 'p', text: 'SOEM es una plataforma para **evaluar la seguridad de eventos masivos**. Sirve para revisar que un evento (un concierto, una gala, una feria) cumpla con todo lo necesario en materia de seguridad antes, durante y después de que ocurra.' },
      { t: 'p', text: 'La idea es simple: alguien de tu equipo va al lugar, hace una **revisión completa con una lista de verificación**, toma fotos como evidencia, marca los puntos importantes en un mapa, y al final se genera un **reporte profesional** que se entrega al cliente.' },
      { t: 'h', text: 'Las dos formas de usar SOEM' },
      { t: 'list', items: [
        '**La plataforma web** (admin.soempg.com) → para administrar todo desde una computadora: clientes, proyectos, eventos, revisar y publicar reportes.',
        '**La app móvil** (iPhone y Android) → para el trabajo en campo: llenar las revisiones, tomar fotos, pasar lista de asistencia.',
      ] },
      { t: 'h', text: 'Cómo se organiza todo' },
      { t: 'p', text: 'En SOEM todo se conecta en cadena, de lo más grande a lo más chico:' },
      { t: 'flow', text: 'CLIENTE  →  PROYECTO  →  EVENTO  →  EVALUACIÓN  →  REPORTE' },
      { t: 'list', items: [
        '**Cliente** = la empresa que te contrata (ej. una promotora de conciertos)',
        '**Proyecto** = el trabajo contratado por ese cliente (ej. "Gira 2026")',
        '**Evento** = un evento concreto dentro del proyecto (ej. "Concierto en el Estadio Azteca")',
        '**Evaluación** = la revisión de seguridad de ese evento (la lista de verificación llenada)',
        '**Reporte** = el documento PDF final que se le entrega al cliente',
      ] },
      { t: 'tip', text: 'Entender esta cadena te ayuda a saber siempre dónde estás parado dentro de la plataforma.' },
    ],
  },

  {
    id: 'roles',
    title: 'Roles y permisos',
    icon: ShieldCheck,
    roles: ALL_ROLES,
    summary: 'Quién es quién: Super Admin, Líder, Levantador y Cliente.',
    blocks: [
      { t: 'p', text: 'Lo que ves en SOEM depende de tu rol. Hay cuatro, ordenados por jerarquía:' },
      { t: 'table', headers: ['Rol', 'Qué hace'], rows: [
        ['**Super Administrador**', 'Manda en todo: usuarios, plantillas, configuración, auditoría. Puede "ver como" cualquier otro usuario (Modo Espejo).'],
        ['**Líder de Proyecto**', 'Maneja clientes, proyectos y eventos. Revisa las evaluaciones y decide si se publican o se regresan.'],
        ['**Levantador**', 'Es quien va a campo: llena las revisiones, sube fotos, marca el mapa, pasa lista de asistencia.'],
        ['**Cliente**', 'Solo consulta: ve los reportes ya publicados de su empresa, sus incidentes y su mapa.'],
      ] },
      { t: 'tip', text: 'Si entras a una sección que no te corresponde, aparece "Acceso Denegado". Es normal: significa que esa parte es para otro rol.' },
      { t: 'p', text: 'El flujo de negocio entre roles: el **Super Admin** crea clientes, proyectos, plantillas y usuarios. El **Líder** asigna levantadores, crea evaluaciones y las revisa. El **Levantador** llena el checklist en campo. El **Cliente** consulta el resultado publicado.' },
      { t: 'h', text: 'Tu cuenta y tu sesión' },
      { t: 'p', text: 'Al iniciar sesión recibes una llave digital que dura **1 hora**; la plataforma la renueva sola en segundo plano hasta 7 días sin pedirte la contraseña. Cerrar sesión la invalida de inmediato.' },
      { t: 'p', text: 'Las cuentas tienen cuatro estados: **activo**, **inactivo**, **suspendido** y **temporal** (con fecha de vencimiento; al vencer, el acceso se corta solo). Solo un Super Admin crea usuarios — no hay registro público.' },
      { t: 'warn', text: 'Si fallas la contraseña **5 veces**, la cuenta se bloquea y los Super Admins reciben un correo de alerta. Un administrador debe reactivarla (al ponerla en "activo" el contador se reinicia solo).' },
      { t: 'p', text: 'Para recuperar contraseña se envía un enlace que **vence en 1 hora y solo sirve una vez**. Por seguridad la pantalla siempre responde lo mismo exista o no el correo, para no revelar qué cuentas existen.' },
      { t: 'tip', text: 'Cada usuario puede tener un **jefe directo** asignado; con eso se arma el Organigrama. El sistema rechaza ciclos (alguien no puede ser jefe de su propio jefe).' },
    ],
  },

  {
    id: 'dashboard',
    title: 'Dashboard',
    icon: LayoutDashboard,
    roles: ALL_ROLES,
    summary: 'La pantalla de inicio: tu resumen según tu rol.',
    blocks: [
      { t: 'p', text: 'Es lo primero que ves al entrar. Es un resumen rápido y cambia según tu rol:' },
      { t: 'list', items: [
        '**Admin o Líder** → tarjetas con tus números clave (proyectos activos, evaluaciones en revisión, clientes activos, evaluaciones bloqueadas), gráficas, y accesos directos a lo más reciente.',
        '**Levantador** → tus pendientes: borradores sin terminar, evaluaciones que te regresaron, tus proyectos y eventos asignados.',
        '**Cliente** → tu "Portal de Evaluaciones": locaciones evaluadas, reportes publicados, incidentes y la evolución de tus calificaciones.',
      ] },
      { t: 'tip', text: 'La primera vez que entras aparece un **tour de bienvenida**. Lo puedes volver a ver cuando quieras desde tu perfil.' },
    ],
  },

  {
    id: 'clientes',
    title: 'Clientes',
    icon: Building2,
    roles: ['super_admin', 'lider_proyecto'],
    summary: 'El directorio de empresas que contratan el servicio.',
    blocks: [
      { t: 'p', text: '**Para qué sirve:** es tu directorio de empresas que contratan el servicio. Todo lo demás (proyectos, eventos, evaluaciones) cuelga de un cliente.' },
      { t: 'h', text: 'Agregar un cliente' },
      { t: 'steps', items: [
        'Click en **"Nuevo Cliente"**',
        'Llena el formulario: logo, nombre, razón social, giro, tamaño, datos de contacto, dirección, responsable de seguridad, contacto de emergencia 24/7, coordenadas, RFC y representante legal',
        'Guardar',
      ] },
      { t: 'p', text: 'Después puedes editarlo, activarlo o desactivarlo, archivarlo y buscarlo. La lista se puede ver en formato de tarjetas o de lista.' },
    ],
  },

  {
    id: 'proyectos',
    title: 'Proyectos',
    icon: FolderKanban,
    roles: MGMT,
    summary: 'El trabajo contratado por un cliente: agrupa eventos y evaluaciones.',
    blocks: [
      { t: 'p', text: '**Para qué sirve:** un proyecto es el trabajo contratado por un cliente. Agrupa todos los eventos y evaluaciones de ese trabajo.' },
      { t: 'h', text: 'Crear un proyecto' },
      { t: 'steps', items: [
        'Click en **"Nuevo Proyecto"**',
        'Pon el nombre, elige el cliente, asigna un líder y uno o varios levantadores',
        'Agrega descripción y fecha límite',
        'Guardar',
      ] },
      { t: 'h', text: 'Dentro del proyecto' },
      { t: 'p', text: 'Ves contadores (cuántas evaluaciones, cuántas publicadas, cuántas bloqueadas, cuántos eventos), una gráfica de cómo va mejorando la calificación, el dictamen del último reporte, y la lista de eventos.' },
      { t: 'list', items: [
        'Crear un evento nuevo o **vincular** un evento que ya existe',
        'Editar el proyecto y limitar qué plantillas se pueden usar',
        '**Comparar evaluaciones** cuando hay dos o más publicadas (para ver si mejoró)',
      ] },
    ],
  },

  {
    id: 'eventos',
    title: 'Eventos',
    icon: CalendarDays,
    roles: MGMT,
    summary: 'Un evento concreto en una locación, con su personal, evaluaciones y mapa.',
    blocks: [
      { t: 'p', text: '**Para qué sirve:** un evento es un evento concreto que va a ocurrir en una locación (un concierto, una función, una feria).' },
      { t: 'h', text: 'Las pestañas de un evento' },
      { t: 'list', items: [
        '**Información General** → cliente, tipo de evento, riesgo proyectado, aforo esperado, capacidad, líder y levantador asignados, locación',
        '**Evaluaciones** → todas las revisiones de seguridad del evento',
        '**Personal** → el staff asignado al evento',
        '**Ubicación** → el mapa con marcadores, servicios cercanos y servicios de emergencia',
      ] },
      { t: 'p', text: 'Arriba hay un cronograma visual y botones para mover el evento por sus etapas: **planeación → en curso → finalizado → auditado**.' },
      { t: 'tip', text: 'Cada evento recibe un código único (**EVT-0001**, EVT-0002...) al crearse, y puede generar su **código QR** para el autoregistro de personal (ver módulo "Registro QR y asistencia").' },
      { t: 'h', text: 'Las 5 fechas del evento' },
      { t: 'p', text: 'Se capturan cinco fechas que deben ir en **orden lógico**: montaje → apertura de puertas → inicio → fin → desmontaje. Si las pones desordenadas, el sistema rechaza el guardado.' },
      { t: 'p', text: 'Además se captura el **perfil de audiencia** (rangos de edad y tipos como VIP, prensa, consumo de alcohol) y el **manejo de multitud** (parados, sentados o híbrido), porque afectan el riesgo proyectado del evento.' },
      { t: 'warn', text: 'Solo se puede **desvincular personal** de un evento mientras está en etapa de "planeación"; al hacerlo se borran también sus registros de asistencia.' },
    ],
  },

  {
    id: 'evaluaciones',
    title: 'Evaluaciones',
    icon: ClipboardList,
    roles: MGMT,
    summary: 'El proceso estrella: crear, llenar, revisar y publicar una evaluación.',
    blocks: [
      { t: 'p', text: 'Esta es la razón de ser de SOEM. El flujo completo de punta a punta:' },
      { t: 'h', text: 'Paso 1 — Crear la evaluación (Líder o Admin)' },
      { t: 'p', text: 'En el evento, abre **Evaluaciones → Nueva Evaluación**. Elige la **plantilla**, ' +
        'la **etapa (Antes, Durante o Después)** y un **levantador del equipo**. ' +
        'Usa **Agregar al equipo** para incorporar otro levantador antes de asignarlo.' },
      { t: 'h', text: 'Paso 2 — Llenar la evaluación (Levantador)' },
      { t: 'p', text: 'El levantador abre la evaluación con el botón **"Contestar"**. La lista está dividida en secciones. Cada criterio se responde según su tipo: escala de 0 a 10, sí/no, opción única o múltiple, texto, número o fecha. En cada criterio se pueden poner **notas** y subir **evidencia** (fotos, video o audio, hasta 25 MB por archivo).' },
      { t: 'p', text: 'Todo se **guarda solo** mientras avanzas. Ves en tiempo real el puntaje, el semáforo de riesgo y el progreso. Cuando todos los criterios tienen respuesta, la evaluación se marca como **"Completada, lista para enviar"**.' },
      { t: 'warn', text: '**Criterios bloqueantes:** algunos puntos son tan importantes que si los calificas en 0, la evaluación queda **"BLOQUEADA"** y no se podrá aprobar hasta resolverlos. Los seis criterios bloqueantes del catálogo base son: Permisos y Licencias, Liderazgo de Seguridad, Recorrido de Inspección, Programa de Protección Civil, Equipo Contra Incendio y Servicios Médicos.' },
      { t: 'h', text: 'Paso 3 — Enviar a revisión' },
      { t: 'p', text: 'Cuando todos los criterios están contestados, el levantador da **"Enviar"**. La evaluación pasa a "en revisión" y le llega un aviso por correo al líder.' },
      { t: 'h', text: 'Paso 4 — Revisar (Líder)' },
      { t: 'p', text: 'El líder abre la evaluación con **"Revisar"**. Ve el puntaje global, una gráfica por sección, todos los criterios con sus respuestas y evidencias, y el mapa de la locación. Entonces decide:' },
      { t: 'list', items: [
        '**Aprobar y Publicar** → la evaluación queda lista y visible para el cliente',
        '**Regresar al Levantador** → si algo está mal; debe escribir comentarios (el levantador recibe un aviso)',
      ] },
      { t: 'tip', text: 'Cuando un **Super Admin o Líder envía su propia evaluación**, esta se publica directo sin pasar por revisión (no tiene sentido que se auto-revisen). El Levantador siempre pasa por revisión.' },
      { t: 'h', text: 'Paso 5 — Reporte publicado' },
      { t: 'p', text: 'Al publicar se genera una copia fija del **Reporte oficial**, con folio, resumen ' +
        'ejecutivo y detalle técnico en un solo PDF. El cliente puede descargarla desde la ' +
        'plataforma cuando esté disponible.' },
      { t: 'warn', text: 'Al publicar una evaluación, las **demás versiones en borrador o regresada del mismo proyecto se archivan automáticamente**. Si tenías trabajo a medias en otra versión, se archiva.' },
      { t: 'h', text: 'Los estados de una evaluación' },
      { t: 'flow', text: 'borrador → en revisión → publicada\n              ↓\n          regresada → vuelve a borrador (editable de nuevo)' },
      { t: 'p', text: 'Cada evaluación tiene versiones (v1, v2...). Esto permite volver a evaluar el mismo evento más adelante y **comparar si mejoró**. Pueden coexistir varias evaluaciones en revisión dentro del mismo proyecto.' },
      { t: 'h', text: 'Cómo se calcula la calificación' },
      { t: 'p', text: 'Cada criterio se califica en escala **0/2/4/6/8/10**. Las secciones no pesan igual: el puntaje global es un promedio ponderado.' },
      { t: 'table', headers: ['Sección', 'Peso en la calificación'], rows: [
        ['Evaluación de Eventos', '25%'],
        ['Seguridad (Security)', '35%'],
        ['Protección Civil (Safety)', '40%'],
      ] },
      { t: 'p', text: 'El porcentaje global define el semáforo: **crítico** hasta 40, **alto** de 41 a 60, **medio** de 61 a 80 y **bajo** de 81 a 100.' },
      { t: 'tip', text: 'Antes de publicar, el líder y super_admin pueden abrir **Vista previa — BORRADOR ' +
        '(no oficial)** desde la revisión. Las evaluaciones archivadas conservan la ' +
        'descarga de su copia oficial disponible.' },
    ],
  },

  {
    id: 'plantillas',
    title: 'Constructor de Plantillas',
    icon: Layers,
    roles: ['super_admin'],
    summary: 'Donde se arman las listas de verificación que usan las evaluaciones.',
    blocks: [
      { t: 'p', text: 'El Constructor es donde se arman las **listas de verificación** que después se usan para evaluar. Solo el **Super Administrador** puede usarlo. Piénsalo así: una plantilla es el "examen" de seguridad; cada evaluación contesta una copia de una plantilla.' },
      { t: 'h', text: 'Cómo se organiza una plantilla' },
      { t: 'flow', text: 'PLANTILLA  →  SECCIONES  →  PREGUNTAS (criterios)' },
      { t: 'h', text: 'Los 4 estados de una plantilla' },
      { t: 'table', headers: ['Estado', 'Qué significa', '¿Se puede editar?'], rows: [
        ['**Borrador**', 'Se está armando, todavía no se usa', 'Sí, libremente'],
        ['**Activa**', 'Está en uso: con ella se crean las evaluaciones nuevas', 'No (hay que crear una nueva versión)'],
        ['**Retirada**', 'La reemplazó una versión nueva. Ya no se usa para evaluaciones nuevas, pero las que ya la usan siguen con ella', 'No'],
        ['**Archivada**', 'Ya no se usa', 'No'],
      ] },
      { t: 'warn', text: '**Las plantillas activas no se editan.** Si cambiaras una plantilla en uso, alterarías evaluaciones en curso. Para modificar una activa: **Nueva versión** → editar el borrador → **Activar plantilla**. Al activarla, la versión anterior queda **retirada**; si era la plantilla por defecto, la nueva la reemplaza, y los proyectos que la tenían permitida pasan a la nueva. **Duplicar** sigue existiendo para crear una plantilla independiente.' },
      { t: 'h', text: 'El layout del Constructor' },
      { t: 'list', items: [
        '**Barra superior** → nombre, estado, y los botones Vista previa, Importar JSON y Activar plantilla',
        '**Columna izquierda** → las Secciones (agregar, reordenar arrastrando, renombrar, eliminar)',
        '**Columna central** → las Preguntas de la sección seleccionada',
        '**Columna derecha** → la configuración de la pregunta seleccionada',
      ] },
      { t: 'h', text: 'Configuración de una pregunta' },
      { t: 'p', text: 'Toda pregunta tiene: **Título** (obligatorio), **Descripción/Enfoque**, **Recinto Seguro** (el estándar del criterio, aparece en una caja gris al contestar), **Tipo de respuesta**, y los interruptores de comportamiento: ¿Es requerida?, ¿Permite notas?, ¿Requiere evidencia?, y —solo en la escala de puntaje— ¿Suma al puntaje? y ¿Es bloqueante? (este último solo si suma). Una pregunta que no es requerida no impide enviar la evaluación.' },
      { t: 'h', text: 'Los 10 tipos de pregunta' },
      { t: 'table', headers: ['Tipo', 'Qué es', '¿Suma al puntaje?'], rows: [
        ['Escala de puntaje', 'Escala numérica 0-10 (el tipo principal de SOEM). El botón "Cargar escala 0-10" precarga los 6 niveles oficiales.', 'Sí (si "¿Suma al puntaje?" está activo)'],
        ['Opción única', 'Elegir una sola opción de una lista', 'No (informativo)'],
        ['Opción múltiple', 'Elegir varias opciones a la vez', 'No (informativo)'],
        ['Lista desplegable', 'Igual que opción única pero en menú desplegable', 'No (informativo)'],
        ['Sí / No', 'Respuesta binaria', 'No (informativo)'],
        ['Numérico', 'Solo números, con rango mín/máx opcional', 'No (informativo)'],
        ['Texto corto', 'Una línea (un folio, una referencia)', 'No'],
        ['Texto largo', 'Varias líneas (observaciones, justificaciones)', 'No'],
        ['Fecha', 'Selector de calendario (vigencias, inspecciones)', 'No'],
        ['Archivo', 'Adjuntar foto o documento', 'No'],
      ] },
      { t: 'h', text: 'La escala de puntaje en detalle' },
      { t: 'p', text: 'Cada nivel de la escala tiene 4 campos: **Valor** (el número que suma), **Etiqueta del valor** (ej. "Implementado según estándar"), **Descripción del estado** (qué significa ese nivel en ese criterio) y **Recomendación automática** (lo que se le sugiere al evaluador si elige ese valor). Los 6 niveles oficiales SOEM: No cumple · Condiciones por debajo del límite · Condiciones limitadas · Implementado según estándar · Implementado con resultados consistentes · Condición exportable.' },
      { t: 'h', text: 'Importar JSON y Vista previa' },
      { t: 'p', text: 'El botón **"Importar JSON"** carga una plantilla completa de un solo archivo: crea las secciones y preguntas que falten y actualiza las existentes (conservando respuestas). Solo está disponible en plantillas en **borrador**: para importar sobre una activa, crea primero una nueva versión. **"Vista previa"** muestra cómo se verá la plantilla al contestarla, sin guardar nada.' },
    ],
  },

  {
    id: 'mapa',
    title: 'Editor de Ubicación (mapa)',
    icon: Map,
    roles: ALL_ROLES,
    summary: 'Armar el plano de seguridad de un lugar: puntos, áreas, rutas y PDF.',
    blocks: [
      { t: 'p', text: 'Una de las herramientas más potentes de SOEM. Sirve para armar el **plano de seguridad** de un lugar. Lo encuentras en la pestaña **"Ubicación"** de cualquier locación o evento.' },
      { t: 'h', text: 'Lo que ves' },
      { t: 'list', items: [
        '**A la izquierda** → el menú de objetos (más de 200 cosas que puedes poner en el mapa) con buscador',
        '**En el centro** → el mapa',
        '**Abajo** → las listas de servicios cercanos y de emergencia',
      ] },
      { t: 'h', text: 'Agregar un punto' },
      { t: 'steps', items: [
        'Abre una categoría del menú izquierdo (Áreas, Infraestructura, Personal) o usa el buscador',
        'Click en el objeto que quieres (ej. "Cámara CCTV")',
        'Click en el mapa, en el lugar exacto — el objeto aparece como alfiler con su ícono de color',
      ] },
      { t: 'tip', text: 'El mapa **no se mueve** al agregar puntos: te quedas justo donde estabas.' },
      { t: 'h', text: 'Áreas y rutas' },
      { t: 'list', items: [
        '**Áreas (zonas)** → para marcar área de público, zona VIP, estacionamiento. Click en cada esquina, doble click para cerrar.',
        '**Rutas (líneas)** → rutas peatonales, de evacuación o accesos vehiculares. Click en cada punto, doble click para terminar.',
        'Al hacer click en un área o ruta ves sus medidas: superficie en m², perímetro, longitud.',
        '**Mover** un alfiler arrastrándolo; **deformar** un área arrastrando sus esquinas; **eliminar** con el botón rojo.',
      ] },
      { t: 'h', text: 'Exportar a PDF' },
      { t: 'p', text: 'El botón **"Exportar PDF"** genera un reporte profesional: encabezado con cliente, ubicación, fecha y quién lo preparó; la imagen del mapa con todos los objetos y su nombre; tablas con puntos, áreas y rutas; pie de página con fecha y número de página.' },
      { t: 'h', text: 'Servicios cercanos y de emergencia' },
      { t: 'list', items: [
        '**Servicios cercanos** → un botón busca con Google Maps los hospitales, gasolineras, hoteles y restaurantes alrededor. Eliges cuáles guardar.',
        '**Servicios de emergencia** → los que ya tienes registrados en SOEM. Los vinculas a la locación para que se hereden a todos sus eventos.',
      ] },
      { t: 'h', text: '"Del venue" vs "Solo este evento"' },
      { t: 'p', text: 'Cuando abres el mapa desde un evento verás dos tipos de objetos: **Del venue** (heredados de la locación, aparecen en todos los eventos de ese lugar) y **Solo este evento** (exclusivos del evento que estás viendo).' },
      { t: 'tip', text: 'El botón de pantalla completa (arriba a la derecha) te da más espacio para trabajar; sales con la tecla ESC.' },
    ],
  },

  {
    id: 'directorio',
    title: 'Directorio: catálogos compartidos',
    icon: Users,
    roles: MGMT,
    summary: 'Personal, Locaciones, Emergencias, Autoridades y Catálogo de Equipo.',
    blocks: [
      { t: 'p', text: 'Son las listas que se usan en toda la plataforma:' },
      { t: 'list', items: [
        '**Personal (Staff)** → directorio de trabajadores. Se dan de alta con un formulario de varios pasos (fotos, datos, equipo asignado). Después se asignan a eventos.',
        '**Locaciones** → los lugares donde ocurren los eventos (estadios, auditorios, centros de convenciones). Cada locación tiene su propio mapa, que los eventos heredan.',
        '**Emergencias** → servicios de emergencia: hospitales, bomberos, Cruz Roja, etc. Se vinculan a locaciones y eventos.',
        '**Autoridades** → policía, protección civil y demás autoridades, vinculables a eventos y locaciones.',
        '**Catálogo de Equipo** → la lista de equipo (radios, chalecos, conos...) que luego se asigna al personal. Solo Super Admin.',
      ] },
      { t: 'tip', text: 'Los levantadores también pueden dar de alta servicios de emergencia y autoridades desde campo, pero **no tienen acceso al módulo de Personal**.' },
    ],
  },

  {
    id: 'personal',
    title: 'Personal y staff de evento',
    icon: Users,
    roles: ['super_admin', 'lider_proyecto'],
    summary: 'Expedientes del personal, asignación a eventos, pagos, equipo y asistencia.',
    blocks: [
      { t: 'h', text: 'El expediente de una persona' },
      { t: 'p', text: 'Cada persona del staff recibe un código automático (**PER-0001**, PER-0002...). Su expediente incluye nombre, género, edad, teléfono, correo, puesto, dirección, contacto de emergencia, foto (máx. 5 MB) y tres declaraciones de salud: condición médica, consumo de sustancias y discapacidad.' },
      { t: 'warn', text: 'Si se marca **condición médica** o **discapacidad**, es obligatorio detallar cuál — el sistema no deja guardar sin el detalle.' },
      { t: 'p', text: 'Los datos delicados van **cifrados** en la base de datos: la cuenta bancaria y la CLABE se guardan encriptadas y en pantalla siempre aparecen enmascaradas (****1234). El número de seguro social también va cifrado y solo lo ven completo Líderes y Super Admins.' },
      { t: 'p', text: 'El sistema marca el perfil como **completo** cuando tiene foto, teléfono, contacto de emergencia y cuenta bancaria. En la ficha de cada persona se listan todos los eventos en los que ha participado.' },
      { t: 'h', text: 'Asignar personal a un evento' },
      { t: 'p', text: 'Una asignación une a una persona con un evento y es **única**: no se puede asignar dos veces a la misma persona. Al asignar en lote, si alguien ya estaba asignado se le salta y se te reporta, sin frenar al resto. Cada asignación guarda posición en el evento, monto total a pagar, turnos, ubicación, zona y estado de capacitación.' },
      { t: 'h', text: 'Pagos' },
      { t: 'p', text: 'Se registran **abonos parciales** con notas y comprobante. El sistema valida que el abono no exceda el saldo y actualiza el estado solo: **pendiente → parcial → pagado**, guardando el historial completo de cada pago.' },
      { t: 'h', text: 'Equipo' },
      { t: 'p', text: 'El equipo se entrega desde el catálogo (uniforme, radio, lámpara...) registrando cantidad y condición. Al devolverlo se valida que no devuelvas más de lo entregado y se captura la condición: bueno, regular, malo, perdido o dañado. **Perdido o dañado** marca el artículo como "requiere atención".' },
      { t: 'h', text: 'Asistencia' },
      { t: 'p', text: 'El pase de lista es por día: presente, ausente, tardanza o justificado, con hora de entrada y salida. Hay un registro por persona por fecha (si se repite, se actualiza). Los levantadores pueden pasar lista; el resto de operaciones exige Líder o superior.' },
      { t: 'tip', text: 'La tasa de asistencia cuenta **"presente" y "tardanza"** como días trabajados. Hay un reporte PDF del personal del evento con pagos, saldos, porcentaje de asistencia y equipo pendiente.' },
    ],
  },

  {
    id: 'incidentes',
    title: 'Incidentes',
    icon: ShieldAlert,
    roles: ['super_admin', 'lider_proyecto', 'cliente'],
    summary: 'Registro de hallazgos: severidad, ciclo de vida y quién puede verlos.',
    blocks: [
      { t: 'p', text: 'Un incidente pertenece a un proyecto y opcionalmente se liga a una evaluación. Se captura título, descripción, **severidad** (menor, mayor o crítico) y evidencias.' },
      { t: 'p', text: 'Nace en estado **abierto** y avanza a **en proceso** y **cerrado**. Al cerrarlo, el sistema estampa la fecha de resolución automáticamente. Crear y editar incidentes exige rol Líder de Proyecto o superior.' },
      { t: 'h', text: 'Quién ve qué (reglas estrictas)' },
      { t: 'list', items: [
        '**Levantadores** → no tienen acceso al detalle de incidentes. Es información sensible.',
        '**Líderes** → solo ven los incidentes de los proyectos que dirigen; un líder no puede consultar incidentes de proyectos ajenos.',
        '**Clientes** → ven únicamente incidentes de severidad **mayor o crítica** de sus proyectos. Los menores se consideran operación interna.',
        '**Super Admin** → ve todo.',
      ] },
      { t: 'tip', text: 'Cada creación y cada cambio de estado queda registrado en la bitácora de auditoría con el valor anterior y el nuevo.' },
    ],
  },

  {
    id: 'qr',
    title: 'Registro QR y asistencia',
    icon: QrCode,
    roles: MGMT,
    summary: 'Autoregistro de personal con QR y pase de lista el día del evento.',
    blocks: [
      { t: 'h', text: 'Autoregistro con QR' },
      { t: 'p', text: 'Cada evento puede generar un **código QR** único. Cuando alguien lo escanea, se abre un formulario público (sin necesidad de tener cuenta) donde el personal externo se registra solo para ese evento.' },
      { t: 'p', text: 'El registro queda **pendiente de aprobación**. Un líder o admin lo aprueba o lo rechaza. Al aprobarlo, la persona se convierte en parte del personal del evento y queda marcada como "registrada vía QR". Si la persona ya existía en el directorio, se actualiza su ficha en lugar de duplicarla.' },
      { t: 'h', text: 'Pasar lista el día del evento (app móvil)' },
      { t: 'steps', items: [
        'Abre el **escáner QR** de la app (tiene linterna y línea guía)',
        'Según el QR: de un evento → abre el evento; de lista de asistencia → "Registrar ahora" o "Abrir lista"; de una persona → sus datos y "Marcar asistencia"',
        'En **"Pasar lista"** marca a cada persona como Presente, Tardanza, Ausente o Justificado (queda registrada la hora)',
        'Arriba: selector de fecha y turno (Matutino / Vespertino / Nocturno), "Todos presentes", "Limpiar" y contadores en vivo',
        'Toca **"Guardar lista"** — si no hay internet, se guarda en el celular y sube cuando vuelva la señal',
      ] },
    ],
  },

  {
    id: 'portal-cliente',
    title: 'Portal del Cliente',
    icon: Monitor,
    roles: ALL_ROLES,
    summary: 'La vista de solo lectura donde el cliente consulta sus reportes.',
    blocks: [
      { t: 'p', text: 'El cliente entra a una versión de **solo lectura**. Solo ve reportes **ya publicados**:' },
      { t: 'list', items: [
        '**Dashboard** → sus locaciones evaluadas, la evolución de su calificación y la bitácora de incidentes',
        '**Detalle de evaluación** → el reporte de seguridad de cada locación, con evidencias',
        '**Incidentes** → la lista de hallazgos críticos y mayores',
        '**Mapa Interactivo** → el mapa de sus locaciones con los puntos de seguridad',
      ] },
      { t: 'tip', text: 'Los incidentes se generan automáticamente: si un criterio importante salió en 0 es un **incidente crítico**; si salió bajo (1 o 2) es un **incidente mayor**.' },
    ],
  },

  {
    id: 'app-movil',
    title: 'App móvil',
    icon: Smartphone,
    roles: ALL_ROLES,
    summary: 'La herramienta de campo: evaluaciones, fotos, QR, mapa y modo offline.',
    blocks: [
      { t: 'p', text: 'La app móvil es para el **trabajo en campo**: llenar revisiones, tomar fotos, marcar el mapa y pasar lista de asistencia. Se usa la **misma cuenta** que en la web.' },
      { t: 'warn', text: '**Funciona sin internet.** Si estás en un sótano o un estadio sin señal, la app guarda todo en el celular y lo sube sola cuando vuelve la conexión. Arriba siempre ves un letrero que indica si estás en línea o sin conexión. Nunca pierdes tu trabajo.' },
      { t: 'h', text: 'Vista del Levantador — 5 pestañas' },
      { t: 'list', items: [
        '**Inicio** → 4 tarjetas de resumen (Proyectos, Pendientes, Eventos, Bloqueantes), tus evaluaciones pendientes, próximos eventos y la campana de notificaciones',
        '**Proyectos** → tus proyectos con buscador y orden; líderes/admins pueden crear proyectos desde el celular',
        '**Eventos** → lista y creación de eventos (con captura GPS y búsqueda de servicios cercanos); el detalle tiene Info, Evaluaciones, Personal, Emergencias y Mapa',
        '**Eval** → tus evaluaciones en 3 grupos: Pendientes (borrador), Completas (listas para enviar) y Enviadas (en revisión o publicadas)',
        '**Perfil** → tu foto, datos, cambio de contraseña, ayuda y cerrar sesión',
      ] },
      { t: 'tip', text: 'Si además eres líder o admin, un **botón flotante "+"** abre un menú rápido para crear Proyecto, Evento o Evaluación desde cualquier pestaña.' },
      { t: 'h', text: 'Hacer una evaluación en campo' },
      { t: 'steps', items: [
        'Abre la evaluación desde Inicio, la pestaña Eval o el evento',
        'Arriba ves tu avance ("18 de 35 criterios") y el botón "Ir al siguiente"',
        'Los criterios están en pestañas por categoría; las terminadas al 100% se marcan en verde',
        'Responde cada criterio según su tipo; los **bloqueantes** están marcados y si dejas uno en 0 aparece una alerta roja',
        'Sube **evidencia**: Foto (cámara o galería), Video o Nota de voz — se sube cuando hay señal',
        'Todo se guarda solo; arriba ves el porcentaje y el semáforo en vivo',
        'Al terminar, toca **"Enviar a revisión"** — la app te avisa si falta algo o hay un bloqueante en 0',
        'Después de publicada, puedes descargar y compartir el Reporte oficial con folio desde el celular',
      ] },
      { t: 'h', text: 'Vista del Cliente — 5 pestañas' },
      { t: 'list', items: [
        '**Inicio** → 4 tarjetas, semáforo global de seguridad (promedio de todas sus evaluaciones), últimas evaluaciones y próximos eventos',
        '**Evaluaciones** → solo las publicadas, con buscador y filtro por nivel de riesgo; el detalle muestra puntaje, resultados por criterio, recomendaciones y evidencias ampliables',
        '**Eventos** → lista y detalle en solo lectura',
        '**Reportes** → descargar y compartir el Reporte oficial en PDF',
        '**Perfil** → datos, foto y cambio de contraseña',
      ] },
      { t: 'h', text: 'App vs Web' },
      { t: 'table', headers: ['', 'App móvil', 'Plataforma web'], rows: [
        ['Para qué es', 'Trabajo en campo', 'Administración'],
        ['Funciona sin internet', 'Sí', 'No, necesita conexión'],
        ['Escanear QR / pasar lista', 'Sí', 'No'],
        ['Capturar GPS en el lugar', 'Sí', 'No'],
        ['Revisar y publicar reportes', 'Limitado', 'Completo'],
        ['Usuarios, plantillas, auditoría', 'No', 'Sí'],
        ['Editor de mapa', 'Versión simple', 'Completo'],
      ] },
    ],
  },

  {
    id: 'reportes',
    title: 'Reportes PDF',
    icon: FileText,
    roles: ALL_ROLES,
    summary: 'Reporte oficial, vista previa BORRADOR y Reporte final del evento; además, reportes de personal y mapa.',
    blocks: [
      { t: 'p', text: 'El Reporte oficial se genera al publicar y se conserva como copia fija con folio e ' +
        'identidad SOEM. Incluye un resumen ejecutivo y el detalle técnico. La copia ' +
        'vigente se puede descargar incluso durante una regeneración o si esta falla.' },
      { t: 'table', headers: ['Reporte', 'Qué contiene', 'Quién lo puede bajar'], rows: [
        ['**Reporte oficial** (evaluación)',
          'Resumen, resultados por sección, bloqueantes, cadena de hallazgos, riesgos, ' +
          'observaciones y evidencias', 'Cualquiera con acceso a la evaluación, incluido el cliente'],
        ['**Reporte final del evento**', 'Expediente Antes, Durante y Después, cadenas completas de hallazgos y ' +
          'recomendaciones abiertas; botón en Después', 'Quien tenga acceso al evento'],
        ['**Personal del evento**', 'Tabla por persona con pago total, pagado, saldo, % de asistencia y equipo pendiente, más totales globales', 'Líder o superior'],
        ['**Mapa** (locación o evento)', 'Imagen del mapa con marcadores y figuras, tablas de objetos por categoría y métricas de área y longitud', 'Quien tenga acceso al mapa'],
      ] },
      { t: 'warn', text: 'Antes de publicar, el líder y super_admin disponen de **Vista previa — BORRADOR ' +
        '(no oficial)**. El Reporte oficial se descarga cuando la copia está disponible, ' +
        'también si la evaluación fue archivada. El cliente recibe proveedores sin teléfono ' +
        'ni correo, con su responsable visible.' },
      { t: 'tip', text: 'Si el reporte está en generación, vuelve a consultar en unos momentos. Si hay otro ' +
        'reporte en generación, espera y reintenta. En la vista previa puedes usar ' +
        'Descargar BORRADOR si tu navegador no muestra el visor.' },
    ],
  },

  {
    id: 'notificaciones',
    title: 'Notificaciones y correos',
    icon: Bell,
    roles: ALL_ROLES,
    summary: 'Cuándo llega un correo, a quién, y cómo funciona la campana in-app.',
    blocks: [
      { t: 'h', text: 'Correos automáticos' },
      { t: 'p', text: 'La plataforma envía correo en estos momentos:' },
      { t: 'list', items: [
        '**Evaluación enviada a revisión** → al líder del proyecto (si no hay líder con correo, al Super Admin)',
        '**Evaluación regresada** → al levantador, con los comentarios resaltados',
        '**Evaluación publicada** → al levantador y a todos los usuarios cliente de la empresa, con la calificación y el semáforo a color',
        '**Evento creado o asignado** → a líder y levantador',
        '**Cierre del expediente** → requiere evaluación Después publicada y hallazgos finalizados',
        '**Registro QR** → recibido, aprobado o rechazado',
        '**Cuenta** → bienvenida, cambios de estado y alerta de seguridad tras 5 intentos fallidos de login',
      ] },
      { t: 'h', text: 'La campana dentro de la plataforma' },
      { t: 'p', text: 'El feed de la campana se arma al vuelo con los últimos **30 días** (máximo 50 avisos): evaluaciones regresadas o publicadas de tus proyectos, y eventos que te asignaron. Lo único que se guarda es qué ya leíste.' },
      { t: 'tip', text: 'Como el feed es derivado, si un dato cambia (por ejemplo se archiva la evaluación) el aviso viejo simplemente deja de aparecer.' },
      { t: 'warn', text: 'Si el envío de un correo falla al publicar o enviar una evaluación, **la operación no se revierte**: el fallo se registra en auditoría y el flujo continúa. Si esperabas un correo y no llegó, la acción sí se completó.' },
    ],
  },

  {
    id: 'administracion',
    title: 'Administración',
    icon: Settings,
    roles: ['super_admin'],
    summary: 'Usuarios, organigrama, auditoría, archivo, Modo Espejo, FAQ y configuración.',
    blocks: [
      { t: 'list', items: [
        '**Usuarios** → dar de alta personas, asignarles rol y jefe directo, cambiar su estado (activo, inactivo, suspendido, temporal), restablecer contraseñas.',
        '**Organigrama** → el árbol de quién depende de quién.',
        '**FAQ / Ayuda** → el Super Admin administra las preguntas frecuentes; todos los demás las consultan en su sección de Ayuda.',
        '**Configuración** → ajustes generales del sistema.',
      ] },
      { t: 'h', text: 'Auditoría (bitácora)' },
      { t: 'p', text: 'Casi todos los módulos escriben en la bitácora: crear, editar y archivar entidades, cambios de estado de evaluaciones y eventos, asignaciones de personal, pagos, entregas de equipo, cambios de contraseña, entradas a Modo Espejo, aprobaciones QR...' },
      { t: 'p', text: 'Cada registro guarda quién lo hizo (id y correo), la acción, la entidad afectada, **el valor anterior y el nuevo** y la fecha. Se filtra por entidad, acción y usuario, y va del más reciente al más antiguo. Es exclusiva del Super Admin y no se puede editar ni borrar.' },
      { t: 'h', text: 'Archivo (en vez de borrar)' },
      { t: 'p', text: 'SOEM **no borra: archiva**. El elemento se marca con la fecha y quién lo archivó, y desaparece de las listas normales. Aplica a clientes, proyectos, eventos, evaluaciones, locaciones, usuarios, personal y plantillas.' },
      { t: 'warn', text: '**Archivar en cascada:** si intentas archivar un cliente que tiene proyectos activos, el sistema te avisa cuántos proyectos y eventos activos tiene y pide confirmación explícita. Al confirmar, archiva en cadena: cliente → sus proyectos → los eventos de esos proyectos. Lo mismo con proyectos que tienen eventos activos.' },
      { t: 'p', text: 'Restaurar devuelve el elemento a las listas. El panel de Archivo muestra todo lo archivado agrupado por tipo, para revisar o restaurar desde un solo lugar. Cada archivado y restauración queda en auditoría.' },
      { t: 'h', text: 'Modo Espejo' },
      { t: 'p', text: 'Permite al Super Admin "ver la plataforma como" otro usuario para dar soporte: mismos permisos, misma visibilidad, mismos proyectos. Mientras está activo aparece una franja amarilla; con **"Salir"** regresas a tu vista normal.' },
      { t: 'tip', text: 'La entrada al Modo Espejo queda auditada a nombre del admin (quién entró como quién y cuándo), mientras que las acciones que hagas dentro se registran a nombre del usuario suplantado.' },
    ],
  },

  {
    id: 'conceptos',
    title: 'Conceptos clave',
    icon: Lightbulb,
    roles: ALL_ROLES,
    summary: 'Semáforo de riesgo, dictamen, evidencia y criterios bloqueantes.',
    blocks: [
      { t: 'h', text: 'El semáforo de riesgo' },
      { t: 'p', text: 'SOEM traduce el puntaje a un color para que se entienda de un vistazo:' },
      { t: 'table', headers: ['Color', 'Nivel', 'Puntaje', 'Significado'], rows: [
        ['🔴 Rojo', 'Crítico', '0 – 40', 'Riesgo muy alto, atención urgente'],
        ['🟠 Naranja', 'Alto', '41 – 60', 'Riesgo importante'],
        ['🟡 Amarillo', 'Medio', '61 – 80', 'Riesgo moderado'],
        ['🟢 Verde', 'Bajo', '81 – 100', 'Todo en orden'],
      ] },
      { t: 'h', text: 'El dictamen' },
      { t: 'p', text: 'Es la conclusión de una evaluación. Resume si el evento/locación es seguro o qué se necesita corregir.' },
      { t: 'h', text: 'Evidencia' },
      { t: 'p', text: 'Las fotos, videos o notas de voz que se suben en cada criterio para respaldar la calificación. Es lo que le da peso al reporte.' },
      { t: 'h', text: 'Criterios bloqueantes' },
      { t: 'p', text: 'Puntos de seguridad tan importantes que, si salen reprobados (en 0), bloquean toda la evaluación. No se puede aprobar hasta resolverlos.' },
      { t: 'h', text: 'Versiones' },
      { t: 'p', text: 'Cada evaluación tiene versiones (v1, v2...). Permiten volver a evaluar el mismo evento y comparar la evolución.' },
    ],
  },

  {
    id: 'problemas',
    title: 'Problemas comunes',
    icon: LifeBuoy,
    roles: ALL_ROLES,
    summary: 'Qué hacer cuando algo no funciona como esperas.',
    blocks: [
      { t: 'table', headers: ['Problema', 'Qué hacer'], rows: [
        ['No puedo entrar', 'Revisa correo y contraseña. Usa "¿Olvidaste tu contraseña?"'],
        ['Me sale "Acceso Denegado"', 'Esa sección es para otro rol. Es normal.'],
        ['No puedo editar una evaluación', 'Si ya está publicada, queda congelada. Si está en revisión, espera a que el líder la regrese.'],
        ['La evaluación dice "BLOQUEADA"', 'Hay un criterio bloqueante calificado en 0. Hay que resolverlo para poder aprobarla.'],
        ['El mapa no carga', 'Revisa tu conexión y refresca la página.'],
        ['El PDF no se abre', 'Tu navegador puede estar bloqueando ventanas emergentes. Dale permiso.'],
        ['La app no sincroniza', 'Revisa tu conexión. La app sube los datos sola cuando vuelve la señal.'],
        ['No veo el sidebar del mapa', 'Puede que no tengas permisos de edición en ese lugar.'],
      ] },
      { t: 'p', text: '¿Dudas o algo no funciona? Contacta a quien te dio acceso al sistema para que levante un ticket de soporte.' },
    ],
  },
];
